import type { IntegrationSettings } from '@vy/types';
import { INTEGRATION_PROVIDERS } from '@vy/types';
import type { IntegrationsSettings } from '@vy/integrations-types';
import { raise, validateNotNullOrUndefined } from '@vy/utils';
import { useMemo } from 'react';
import { useIntegrationSettings } from '../../../../../../../hooks/requests/use-integration-settings';
import type { HubSpotConfigurationFormValues } from '../HubSpotConfigurationFormSchema';

type FetchHubSpotSettingsResult = {
  integrationId: string;
  configuration: HubSpotConfigurationFormValues;
};

export function useFetchHubSpotSettings(): FetchHubSpotSettingsResult {
  const integrationSettingsResponse = useIntegrationSettings(INTEGRATION_PROVIDERS.hubspot);
  const hubspotIntegrationSettings = integrationSettingsResponse.data;

  validateNotNullOrUndefined(hubspotIntegrationSettings, 'hubspotIntegrationSettings');

  const { integrationId, integrationSettings } = hubspotIntegrationSettings;

  return useMemo(
    () => ({
      integrationId,
      configuration: mapIntegrationSettingsToFormValues(integrationSettings),
    }),
    [integrationId, integrationSettings],
  );
}

function mapIntegrationSettingsToFormValues(
  integrationSettings: IntegrationSettings | IntegrationsSettings,
): HubSpotConfigurationFormValues {
  if (integrationSettings.integrationProvider !== INTEGRATION_PROVIDERS.hubspot) {
    raise(`Invalid integration provider ${integrationSettings.integrationProvider}`);
  }

  const config = integrationSettings.integrationConfiguration;

  if (!config) {
    raise('Integration configuration is missing');
  }

  if ('generalConfig' in config) {
    return {
      autoImportCustomers: config.generalConfig?.autoImportCustomers ?? false,
      autoImportContracts: config.generalConfig?.autoImportContracts ?? false,
    };
  }

  return {
    autoImportCustomers: config.autoImportCustomers ?? false,
    autoImportContracts: config.autoImportContracts ?? false,
  };
}
